import { colors } from '@investown/fe/ui-utils/colors';
import { deprecatedSpacing, spacing } from '@investown/fe/ui-utils/spacing';
import { fontSize } from '@investown/fe/ui-utils/fontSize';

const globals = {
  formElementsHeight: '46px',
  radiusSmall: '5px',
  radiusLarge: '10px',
  radiusExtraLarge: '20px',
  maxInputWidth: '350px',
};

export const theme = {
  color: {
    primary: colors.primary.base,
    primaryDisabled: colors.primary.disabledFloating,
    primaryHover: colors.primary.active,
    primaryFaded: colors.primary.faded,
    success: colors.success.base,
    successFaded: colors.success.faded,
    warning: colors.warning.base,
    warningFaded: colors.warning.faded,
    alert: colors.alert.heavy,
    alertBase: colors.alert.base,
    textColor: colors.text.base,
    titleColor: colors.text.base,
    textFaded: colors.text.faded,
    moreFaded: colors.text.disabled,
    contentBackground: colors.primitive.white,
    bodyBackground: colors.primitive.grey,
    iconBackground: colors.secondary.loading,
    white: colors.primitive.white,
    darkGrey: colors.primitive.darkGrey,
    black: colors.primitive.black,
  },
  borderRadius: {
    small: globals.radiusSmall,
    large: globals.radiusLarge,
    extraLarge: globals.radiusExtraLarge,
  },
  font: {
    size: {
      nano: fontSize.nano,
      mini: fontSize.mini,
      small: fontSize.small,
      medium: fontSize.medium,
      large: fontSize.large,
      extraLarge: fontSize.extraLarge,
      huge: fontSize.huge,
    },
    weight: {
      thin: 100,
      light: 300,
      regular: 400,
      medium: 500,
      semiBold: 600,
      bold: 700,
    },
  },
  icons: {
    size: {
      nano: '12px',
      mini: '15px',
      small: '20px',
      medium: '25px',
    },
  },
  input: {
    height: globals.formElementsHeight,
    borderRadius: globals.radiusSmall,
    maxWidth: globals.maxInputWidth,
    borderColor: colors.primary.faded,
    focusBorderColor: colors.primary.base,
  },
  margins: {
    small: deprecatedSpacing.small,
    medium: deprecatedSpacing.medium,
    large: deprecatedSpacing.large,
    extraLarge: deprecatedSpacing.extraLarge,
    huge: deprecatedSpacing.huge,
    massive: deprecatedSpacing.massive,
  },
  paddings: {
    small: deprecatedSpacing.small,
    medium: deprecatedSpacing.medium,
    large: deprecatedSpacing.large,
    extraLarge: deprecatedSpacing.extraLarge,
    huge: deprecatedSpacing.huge,
    massive: deprecatedSpacing.massive,
  },
  spacing: {
    small: spacing.small,
    medium: spacing.medium,
    regular: spacing.regular,
    large: spacing.large,
    extraLarge: spacing.extraLarge,
    huge: spacing.huge,
    massive: spacing.massive,
  },
  breakpoints: {
    mini: '320px',
    small: '576px',
    medium: '768px',
    large: '992px',
    extraLarge: '1200px',
  },
  button: {
    color: {
      primary: colors.primary.base,
      primaryHover: colors.primary.active,
      highlight: colors.highlight.base,
      highlightHover: colors.highlight.active,
      alertHover: colors.alert.base,
      erroLinkHover: colors.alert.active,
    },
    height: globals.formElementsHeight,
    compactHeight: '36px',
    maxWidth: '350px',
    lineHeight: '23px',
    padding: '5px 20px',
    fontSize: fontSize.small,
    fontWeight: '400',
    borderRadius: globals.radiusSmall,
  },
  widget: {
    borderRadius: globals.radiusLarge,
    background: {
      premium: colors.primitive.black,
      secondary: colors.secondary.base,
      success: colors.success.faded,
      warning: colors.success.faded,
      alert: colors.alert.faded,
    },
    size: {
      tile: '85px',
      medium: '190px',
    },
  },
  container: {
    maxWidth: '1200px',
  },
  form: {
    narrow: '350px',
    wide: '450px',
    ultraWide: '600px',
  },
  tag: {
    color: {
      primary: colors.primary.base,
      alert: colors.alert.base,
      success: colors.success.base,
      warning: colors.warning.base,
      default: colors.text.base,
      highlight: colors.highlight.base,
      secondary: colors.text.faded,
    },
    background: {
      success: colors.success.faded,
      primary: colors.primary.faded,
      secondary: colors.secondary.background,
      alert: colors.alert.faded,
      warning: colors.warning.faded,
      default: colors.primitive.grey,
      highlight: colors.highlight.faded,
    },
    border: {
      default: colors.primitive.darkGrey,
      faded: colors.primitive.grey,
    },
  },
  border: {
    faded: colors.primitive.grey,
    lessFaded: colors.primitive.darkGrey,
  },
  alert: {
    background: {
      primary: colors.secondary.base,
    },
  },
};

export type MarginSizeName = keyof typeof theme.margins;
export type SpacingSizeName = keyof typeof theme.spacing;
export type ColorsName = keyof typeof theme.color;
export type FormSizeName = keyof typeof theme.form;
export type BorderColorsName = keyof typeof theme.border;
export type FontWeightName = keyof typeof theme.font.weight;
export type FontSizeName = keyof typeof theme.font.size;
export type WidgetBackgroundName = keyof typeof theme.widget.background;
